import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import {
    CreateCellClickHandler, CustomCellClickAction, createCellCssClassRules,
    getSwitchButtonColumnOptions, GridCellConfig, postSortHandler, RemoveRowHandler, useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { HRM3050_R01DetailDto } from "./Dto_HRM3050_R01";
import { useHRM3050_R01Store } from "./Store_HRM3050_R01";
import {
    detailHeaderNames_HRM3050_R01, formatHRM3050_R01Time, getHRM3050_R01BreakMinutes, HRM3050_R01GridType,
} from "./Util_HRM3050_R01";
import { useHRM3050_R01CellChange } from "./hooks/HRM3050_R01CellChange";

// 휴게 그리드의 컬럼 배치·너비를 브라우저에 보관할 때 사용하는 키이다.
const DETAIL_COLUMN_STATE_KEY = 'HRM3050_R01-detail-grid-column-state';
// 클릭·키보드로 편집을 시작할 수 있는 휴게 컬럼 목록. 렌더링마다 바뀌지 않으므로 컴포넌트 밖 상수로 둔다.
const EDITABLE_COLUMNS = ['breakNm', 'fromTime', 'toTime', 'remark'];

/** Container에서 전달받는 값의 모양이다. */
interface Props {
    clickable: boolean; // true이면 행 클릭을 선택으로 처리한다. +/- 버튼 직후에는 false가 되어 같은 클릭을 한 번 무시한다.
    setClickable: (clickable: boolean) => void; // clickable 값을 바꾸는 함수
    gridRef: React.RefObject<AgGridReact<HRM3050_R01DetailDto> | null>; // 휴게 그리드 인스턴스를 가리키는 ref. 편집 확정에 사용한다.
    defaultHRM3050_R01DetailRow: HRM3050_R01DetailDto; // 신규 행과 빈 행을 만들 때 쓰는 기본값
    handleInsertClick: (event: React.MouseEvent, rowIndex?: number, gridType?: HRM3050_R01GridType) => void; // 행 추가 함수. rowIndex가 있으면 해당 행 아래에 추가한다.
}

/**
 * 선택 근무조의 휴게시간을 편집 그리드로 렌더링한다.
 * 시간(분)과 처리 설명은 저장하지 않는 화면 계산 컬럼이고, 하단 합계 행에 건수와 시간(분) 합계를 표시한다. (실행 샘플과 같음)
 * @param gridRef Content와 공유하여 편집 확정에 사용한다.
 * @param defaultHRM3050_R01DetailRow 신규 행과 그리드 기본 행의 초기값이다.
 * @returns 선택·삭제 표시, 셀 편집 이벤트가 연결된 휴게 그리드 JSX
 */
export const DetailTable_HRM3050_R01 = (props: Props) => {
    // 구조 분해: props 객체에서 필요한 값을 각각의 변수로 꺼낸다.
    const { clickable, setClickable, gridRef, defaultHRM3050_R01DetailRow, handleInsertClick } = props;

    // 화면 Store에서 휴게 목록, 선택·삭제 식별값 목록, 원본 목록, 컬럼 상태, 스크롤 위치와 변경 함수를 가져온다.
    const {
        HRM3050_R01DetailList, setHRM3050_R01DetailList, removeHRM3050_R01DetailList, updateHRM3050_R01DetailList,
        selectedHRM3050_R01DetailList, addSelectedHRM3050_R01DetailList, removeSelectedHRM3050_R01DetailList,
        initSelectedHRM3050_R01DetailList, allSelectedHRM3050_R01DetailList,
        deletedHRM3050_R01DetailList, addDeletedHRM3050_R01DetailList,
        originalHRM3050_R01DetailList,
        detailColumnState, setDetailColumnState, detailScrollTop, setDetailScrollTop,
    } = useHRM3050_R01Store();

    // true이면 행을 드래그하는 중이다. 드래그 중에는 정렬 후 처리를 건너뛴다.
    const isRowDraggingRef = useRef(false);

    // 그리드 기본 설정: 행 ID 생성, 행·셀 CSS 규칙 생성 함수, 빈 행을 채운 표시 목록, 행 번호 칸 렌더러, 스위치 핸들러를 받는다.
    const {
        getRowId, createRowClassRules, createCellClassRules, paddedGridData,
        renderIndexCell, handleSwitchButtonHandler,
    } = useGridSettingContent<HRM3050_R01DetailDto>({
        gridRef,
        // 각 행을 구분하는 기준 필드
        key: 'valueIndex',
        gridData: HRM3050_R01DetailList,
        defaultGridData: defaultHRM3050_R01DetailRow,
        selectedRow: selectedHRM3050_R01DetailList,
        originalGridData: originalHRM3050_R01DetailList,
        updateRow: updateHRM3050_R01DetailList,
    });

    // 신규·기존 행별 비활성 셀과 포인터·정렬 스타일을 정의한다. 처음 한 번만 만든다.
    const cssCellConfig = useMemo<GridCellConfig>(() => ({
        // 모든 행에서 비활성(회색)으로 표시할 컬럼. 순번은 서버가 붙이고, 시간(분)·처리는 계산 컬럼이다.
        disabledCells: ['breakSeq', 'breakMinutes', 'breakHow'],
        // 신규 행에서만 비활성으로 표시할 컬럼
        insertedDisabledCells: [],
        // 기존 행에서만 비활성으로 표시할 컬럼
        commonDisabledCells: [],
        // 모든 행에서 손가락 커서를 표시할 컬럼
        pointerCells: ['breakNm', 'fromTime', 'toTime', 'excludeYn', 'useYn', 'remark'],
        // 신규 행에서만 손가락 커서를 표시할 컬럼
        insertedPointerCells: [],
        // 기존 행에서만 손가락 커서를 표시할 컬럼
        commonPointerCells: [],
        // 왼쪽 정렬할 컬럼
        alignLeftCells: ['breakNm', 'breakHow', 'remark'],
    }), []);

    // 위 설정을 신규 행용·기존 행용 목록으로 합친 결과를 구조 분해로 꺼낸다.
    const {
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns,
    } = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

    // 모든 컬럼에 적용할 기본 설정에 셀 CSS 규칙(선택·삭제·비활성·포인터·정렬)을 더한 객체
    // 선택·삭제 값은 문자열로 저장되므로 숫자 valueIndex와 비교할 때 숫자로 바꾼다.
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM3050_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedHRM3050_R01DetailList.map(Number),
            deletedList: deletedHRM3050_R01DetailList.map(Number),
            disabledInsertedColumns, disabledColumns,
            pointerInsertedColumns, pointerColumns, alignLeftColumns,
        }),
    }), [createCellClassRules, selectedHRM3050_R01DetailList, deletedHRM3050_R01DetailList,
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

    /**
     * 행 번호 칸의 - 버튼 동작이다. 공통 함수가 마지막 1행 삭제를 막고, 신규 행은 즉시 제거·기존 행은 삭제 표시한다.
     * @param rowData - 버튼을 누른 행
     * @returns 반환값은 없다.
     */
    const handleRemoveRow = useCallback((rowData: HRM3050_R01DetailDto) => {
        RemoveRowHandler(gridRef, HRM3050_R01DetailList, rowData, setClickable,
            removeHRM3050_R01DetailList, addDeletedHRM3050_R01DetailList);
    }, [gridRef, HRM3050_R01DetailList, setClickable, removeHRM3050_R01DetailList, addDeletedHRM3050_R01DetailList]);

    // 삭제 표시를 뺀 휴게의 시간(분) 합계. 하단 합계 행에 쓴다.
    // 의존성: 휴게 목록이나 삭제 표시가 바뀔 때만 다시 계산한다.
    const totalBreakMinutes = useMemo(() => {
        const deletedSet = new Set(deletedHRM3050_R01DetailList.map(String));
        return HRM3050_R01DetailList
            .filter(row => !deletedSet.has(String(row.valueIndex)))
            .reduce((sum, row) => sum + getHRM3050_R01BreakMinutes(row), 0);
    }, [HRM3050_R01DetailList, deletedHRM3050_R01DetailList]);

    /** 휴게 그리드 컬럼 정의이다. 순서·이름은 실행 샘플 휴게시간 그리드와 같다. */
    const baseColumnDefs = useMemo<ColDef<HRM3050_R01DetailDto>[]>(() => [
        // 펼치기(...): 행 번호 컬럼 설정 배열의 항목을 이 배열 안에 그대로 풀어 넣는다.
        ...getDefaultRowIndexColumnConfig(
            // 행 번호 칸 렌더러: 번호 또는 D·I·U 표시와 +/- 버튼을 그린다.
            (params: HRM3050_R01DetailDto) => renderIndexCell(
                params,
                // 행 상태 판단: 공통 함수가 삭제·선택·신규·수정 여부를 계산해 반환한다.
                (innerParams: HRM3050_R01DetailDto) => getRowStyleFlags(
                    innerParams,
                    {
                        deletedList: deletedHRM3050_R01DetailList,
                        selectedList: selectedHRM3050_R01DetailList,
                        dataList: HRM3050_R01DetailList,
                    }),
                // + 버튼: 해당 행 아래에 신규 행을 추가한다. Ctrl을 누른 채 클릭하면 10행을 한 번에 추가한다.
                (rowIndex: number, e: React.MouseEvent) => {
                    if (e.ctrlKey) {
                        for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex, 'detail'); }
                    } else {
                        handleInsertClick(e, rowIndex, 'detail');
                    }
                },
                // - 버튼: rowData는 renderIndexCell에서 Dto 형식으로 선언함
                handleRemoveRow,
            ),
        ),
        {
            headerName: detailHeaderNames_HRM3050_R01.breakSeq, colId: 'breakSeq', field: 'breakSeq', width: 70,
            // 순번은 저장할 때 서버가 붙인다. 신규 행은 아직 번호가 없어 빈 칸으로, 합계 행은 건수 문자열 그대로 보여 준다.
            editable: false,
            // ??: 순번이 null이면 빈 문자열을 표시한다.
            valueFormatter: params => String(params.value ?? ''),
        },
        {
            headerName: detailHeaderNames_HRM3050_R01.breakNm, colId: 'breakNm', field: 'breakNm', width: 150,
            editable: true, pasteable: true,
            // 휴게명은 DB 컬럼 길이 50자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 50 },
        },
        {
            headerName: detailHeaderNames_HRM3050_R01.fromTime, colId: 'fromTime', field: 'fromTime', width: 100,
            editable: true, pasteable: true,
            // HH:MM 5자까지 입력받고, 1000처럼 콜론 없이 입력해도 10:00으로 정리한다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 5 },
            valueParser: params => formatHRM3050_R01Time(params.newValue),
        },
        {
            headerName: detailHeaderNames_HRM3050_R01.toTime, colId: 'toTime', field: 'toTime', width: 100,
            editable: true, pasteable: true,
            // 종료가 시작보다 작으면 다음날로 계산한다. 입력 정리는 시작과 같다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 5 },
            valueParser: params => formatHRM3050_R01Time(params.newValue),
        },
        {
            headerName: detailHeaderNames_HRM3050_R01.breakMinutes, colId: 'breakMinutes', width: 90,
            editable: false,
            // 저장하지 않는 계산 컬럼이라 field 없이 값을 만든다. 합계 행은 삭제 표시를 뺀 합계, 데이터 행은 시작~종료 분이다.
            // 삼항 연산자: 하단 고정 행이면 합계를, 아니면 그 행의 휴게 분을 반환한다.
            valueGetter: params => params.node?.rowPinned ? totalBreakMinutes
                : (params.data ? getHRM3050_R01BreakMinutes(params.data) : 0),
            cellStyle: { textAlign: 'right' },
        },
        {
            headerName: detailHeaderNames_HRM3050_R01.excludeYn, colId: 'excludeYn', field: 'excludeYn', width: 90,
            // 근무제외 스위치. Y는 근무·연장·야간 시간에서 빼고, N은 근무로 인정한다.
            ...getSwitchButtonColumnOptions<HRM3050_R01DetailDto>('excludeYn', handleSwitchButtonHandler,
                detailHeaderNames_HRM3050_R01, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: detailHeaderNames_HRM3050_R01.breakHow, colId: 'breakHow', width: 270,
            editable: false,
            // 근무제외 값에 따른 처리 설명이다. 문구는 실행 샘플과 같으며 저장하지 않는다.
            // 삼항 연산자: 합계 행은 빈 칸, 근무제외 Y와 N은 각각의 설명을 표시한다.
            valueGetter: params => params.node?.rowPinned ? ''
                : params.data?.excludeYn === 'Y'
                    ? '근무시간에서 제외 · 지각/조퇴/외출에서 제외'
                    : '근무로 인정 · 지각/조퇴/외출에서만 제외',
        },
        {
            headerName: detailHeaderNames_HRM3050_R01.useYn, colId: 'useYn', field: 'useYn', width: 80,
            // 사용 여부 스위치. N이면 시간 계산과 겹침 검사에서 제외한다.
            ...getSwitchButtonColumnOptions<HRM3050_R01DetailDto>('useYn', handleSwitchButtonHandler,
                detailHeaderNames_HRM3050_R01, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: detailHeaderNames_HRM3050_R01.remark, colId: 'remark', field: 'remark', width: 240,
            editable: true, pasteable: true,
            // 기본 텍스트 편집기를 쓰고 최대 1000자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 1000 },
        },
    ], [renderIndexCell, deletedHRM3050_R01DetailList, selectedHRM3050_R01DetailList, HRM3050_R01DetailList,
        handleInsertClick, handleRemoveRow, totalBreakMinutes, handleSwitchButtonHandler]);

    // 기본 컬럼 순서를 생성하고 저장된 컬럼 상태가 있을 때만 그 상태를 적용한다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    // 삼항 연산자: 컬럼 상태가 없으면 기본 정의를, 있으면 사용자가 저장한 배치를 반영한 정의를 반환한다.
    const columnDefs = useMemo(() => detailColumnState === null ? baseColumnDefs : GridCustomColumnDefs<HRM3050_R01DetailDto>({
        baseColumnDefs, columnState: detailColumnState, headerNames: detailHeaderNames_HRM3050_R01,
        fallbackHeaderClass: aggridHeaderClass,
    }), [baseColumnDefs, detailColumnState]);

    // 셀 값이 바뀌었을 때 휴게 Store 목록에 반영하는 함수를 가져온다.
    const { handleCellChange } = useHRM3050_R01CellChange<HRM3050_R01DetailDto>({ updateRow: updateHRM3050_R01DetailList });
    // 행 클릭 선택 토글 핸들러: 공통 Hook이 합계 행·삭제 행을 제외하고 선택을 추가·해제한다.
    // 공통 Hook은 valueIndex를 숫자 그대로 저장하므로, 선택 목록에는 숫자(행 클릭)와 문자열(전체 선택)이 함께 들어올 수 있다. (HRM3110_R01과 동일)
    const handleRowClick = useRowSelectionHandler({
        dataList: HRM3050_R01DetailList,
        idKey: 'valueIndex',
        selectedList: selectedHRM3050_R01DetailList,
        deletedList: deletedHRM3050_R01DetailList,
        addSelected: addSelectedHRM3050_R01DetailList,
        removeSelected: removeSelectedHRM3050_R01DetailList,
        clickable, setClickable,
    });

    // 방향키 이동과 편집 가능 컬럼 내 키보드 탐색을 공통 이벤트 훅에 연결한다.
    const cellEventHandlers = useDetailTableEvents({
        detailList: HRM3050_R01DetailList, editableColumns: EDITABLE_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    });

    // 셀 클릭 동작 목록: 편집 가능 컬럼을 클릭하면 셀 편집을 시작한다.
    const cellClickActions = useMemo<CustomCellClickAction<HRM3050_R01DetailDto>[]>(() => [{
        // 편집 가능 컬럼이면 true를 반환한다.
        validate: field => EDITABLE_COLUMNS.includes(field),
        // 유효한 클릭의 셀 편집을 시작한다. 반환값은 없다.
        // !: rowIndex와 field가 반드시 있다고 TypeScript에 알려 준다.
        action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
    }], []);

    // 클릭한 셀에 맞는 동작을 찾아 실행하는 셀 클릭 핸들러
    const handleCellClick = CreateCellClickHandler(HRM3050_R01DetailList, 'valueIndex', cellClickActions);
    /**
     * 헤더 정렬 후 원본·현재 행 인덱스를 기준으로 표시 순서를 맞춘다.
     * @param params 정렬된 행 정보
     * @returns 반환값은 없다.
     */
    const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<HRM3050_R01DetailDto>) => {
        if (isRowDraggingRef.current) return;
        postSortHandler(params, HRM3050_R01DetailList, originalHRM3050_R01DetailList, setHRM3050_R01DetailList);
    }, [HRM3050_R01DetailList, originalHRM3050_R01DetailList, setHRM3050_R01DetailList]);

    // 하단 고정 행의 순번 컬럼에 현재 휴게 건수를 표시한다. 시간(분) 합계는 해당 컬럼의 valueGetter가 채운다.
    const pinnedBottomRow = useMemo(() => [{
        breakSeq: HRM3050_R01DetailList.length.toLocaleString() + '건',
        editable: false,
    }], [HRM3050_R01DetailList]);

    // 계산·선택·정렬·편집 이벤트와 컬럼 상태를 연결한 JSX를 반환한다.
    return (
        // 우클릭 메뉴(전체 선택·선택 해제), 셀 계산, 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트
        <GridContextWrapper<HRM3050_R01DetailDto>
            enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            initSelected={initSelectedHRM3050_R01DetailList}
            defaultRowData={defaultHRM3050_R01DetailRow}
            allSelected={allSelectedHRM3050_R01DetailList}
            baseColumnDefs={columnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setDetailColumnState}
            localStorageKey={DETAIL_COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            {/* 휴게 편집 그리드: 행 데이터, 컬럼 정의, 행·셀 CSS 규칙, 클릭·키보드·값 변경·정렬 이벤트, 합계 행을 연결한다. */}
            <Grid_Excel<HRM3050_R01DetailDto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<HRM3050_R01DetailDto>({
                    idKey: 'valueIndex',
                    selectedList: selectedHRM3050_R01DetailList.map(Number),
                    deletedList: deletedHRM3050_R01DetailList.map(Number),
                })}
                onRowClicked={handleRowClick}
                onCellClicked={handleCellClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                onCellValueChanged={handleCellChange}
                pinnedBottomRowData={pinnedBottomRow}
                onPostSortRows={handlePostSortWithDrag}
                columnStateKey={DETAIL_COLUMN_STATE_KEY}
                setColumnState={setDetailColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={detailScrollTop}
                setScrollTop={setDetailScrollTop}
            />
        </GridContextWrapper>
    );
};
